import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  User,
  Mail,
  Phone,
  Building2,
  Briefcase,
  GraduationCap,
  Globe,
  Award,
  Users,
  Target,
  Loader2,
  CheckCircle2,
  BookOpen,
  Calendar
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6, ease: 'easeOut' }
  }
};

const programHighlights = [
  {
    icon: Target,
    title: 'Strategic Thinking',
    description: 'Master the art of systemic leadership and strategic health planning.'
  },
  {
    icon: Users,
    title: 'Peer Network',
    description: 'Connect with health leaders from across Africa and beyond.'
  },
  {
    icon: BookOpen,
    title: 'Rigorous Methodology',
    description: 'Learn our proprietary analytical approach to health systems.'
  },
  {
    icon: Award,
    title: 'Certification',
    description: 'Receive HSL certification upon successful completion.'
  }
];

const programTracks = [
  {
    id: 'executive',
    title: 'Executive Leadership Track',
    duration: '6 months',
    format: 'Hybrid (In-person + Virtual)',
    description: 'For Ministers, Directors General, and Agency Heads seeking to transform national health systems.',
    features: ['1-on-1 Executive Coaching', 'Policy Design Labs', 'International Study Tour', 'Capstone Project']
  },
  {
    id: 'senior',
    title: 'Senior Management Track',
    duration: '4 months',
    format: 'Virtual with 2 In-person Workshops',
    description: 'For Program Directors and Senior Managers leading health initiatives.',
    features: ['Team Leadership Modules', 'Change Management Training', 'M&E Framework Design', 'Peer Learning Groups']
  },
  {
    id: 'emerging',
    title: 'Emerging Leaders Track',
    duration: '3 months',
    format: 'Fully Virtual',
    description: 'For mid-career professionals aspiring to health leadership roles.',
    features: ['Leadership Foundations', 'Health Systems Overview', 'Communication Skills', 'Mentorship Pairing']
  }
];

const leadershipLevelOptions = [
  'Minister / Permanent Secretary',
  'Director General / CEO',
  'Director / Department Head',
  'Senior Manager / Program Lead',
  'Manager / Coordinator',
  'Emerging Professional'
];

const sectorOptions = [
  'Government / Public Sector',
  'International Organization',
  'NGO / Non-profit',
  'Private Healthcare',
  'Academic / Research Institution',
  'Development Partner / Donor',
  'Other'
];

const howHeardOptions = [
  'HSL Website',
  'LinkedIn',
  'Colleague Referral',
  'Conference / Event',
  'Partner Organization',
  'Other'
];

export const LeadershipAcademyEnrollPage = () => {
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    country: '',
    organization: '',
    job_title: '',
    leadership_level: '',
    sector: '',
    program_track: '',
    years_experience: '',
    motivation: '',
    expectations: '',
    how_heard: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const requiredFields = ['full_name', 'email', 'phone', 'country', 'organization', 'job_title', 'leadership_level', 'sector', 'program_track', 'motivation'];
    const missingFields = requiredFields.filter(field => !formData[field]);
    
    if (missingFields.length > 0) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${API}/leadership-academy/enroll`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          enrollment_type: 'leadership_academy',
          submitted_at: new Date().toISOString()
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit enrollment');
      }

      setShowSuccess(true);
      toast.success('Your enrollment application has been submitted!');
    } catch (error) {
      console.error('Enrollment form error:', error);
      toast.error('Failed to submit your enrollment. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (showSuccess) {
    return (
      <div data-testid="enrollment-success-page" className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white border border-slate-200 p-12 max-w-lg text-center"
        >
          <div className="w-20 h-20 bg-teal-500/20 border border-teal-500/50 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 size={40} className="text-teal-600" />
          </div>
          <h2 className="font-heading text-3xl text-slate-900 mb-4">Enrollment Submitted!</h2>
          <p className="text-slate-600 mb-6">
            Thank you for your interest in the HSL Leadership Academy. Our admissions team will 
            review your application and contact you within 5-7 business days with next steps.
          </p>
          <p className="text-slate-500 text-sm mb-8">
            A confirmation email has been sent to <span className="text-teal-600">{formData.email}</span>
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/leadership"
              className="btn-secondary inline-flex items-center justify-center gap-2"
              data-testid="back-to-leadership"
            >
              Back to Leadership Page
            </Link>
            <Link
              to="/"
              className="btn-primary inline-flex items-center justify-center gap-2"
              data-testid="back-to-home"
            >
              Return Home
            </Link>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div data-testid="leadership-academy-enroll-page">
      {/* Hero Section */}
      <section className="section-padding pt-32 bg-gradient-to-b from-slate-100 to-slate-50" data-testid="enroll-hero">
        <div className="container-main">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="max-w-4xl"
          >
            <motion.span 
              variants={itemVariants}
              className="font-mono text-xs uppercase tracking-widest text-teal-600 mb-6 block"
            >
              Leadership Academy Enrollment
            </motion.span>
            
            <motion.h1 
              variants={itemVariants}
              className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-[1.1] mb-6"
            >
              Become an Ambidextrous Leader
            </motion.h1>
            
            <motion.p 
              variants={itemVariants}
              className="text-lg md:text-xl text-slate-600 leading-relaxed"
            >
              Join the HSL Leadership Academy and transform your approach to health systems leadership. 
              Our program equips you with the strategic frameworks, analytical tools, and peer network 
              needed to drive systemic change.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Program Highlights */}
      <section className="section-padding" data-testid="program-highlights">
        <div className="container-main">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {programHighlights.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white border border-slate-200 p-6 hover:border-teal-500 transition-colors group"
                data-testid={`highlight-${index}`}
              >
                <div className="w-12 h-12 bg-teal-100 border border-teal-200 flex items-center justify-center mb-4 group-hover:bg-teal-500 transition-colors">
                  <item.icon size={24} className="text-teal-600 group-hover:text-white transition-colors" />
                </div>
                <h3 className="font-heading text-lg text-slate-900 mb-2">{item.title}</h3>
                <p className="text-slate-500 text-sm leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Program Tracks */}
      <section className="section-padding bg-slate-100" data-testid="program-tracks">
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto mb-12"
          >
            <span className="font-mono text-xs uppercase tracking-widest text-slate-500 mb-4 block">
              Choose Your Path
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-slate-900 mb-4">
              Program Tracks
            </h2>
            <p className="text-slate-600">
              Select the track that best matches your current role and leadership aspirations.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-3 gap-6">
            {programTracks.map((track, index) => (
              <motion.div
                key={track.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white border border-slate-200 p-8 hover:border-teal-500 transition-colors"
                data-testid={`track-${track.id}`}
              >
                <div className="flex items-center gap-3 mb-4">
                  <Calendar size={20} className="text-teal-600" />
                  <span className="text-slate-500 text-sm">{track.duration}</span>
                </div>
                <h3 className="font-heading text-xl text-slate-900 mb-2">{track.title}</h3>
                <p className="text-teal-600 text-sm mb-4">{track.format}</p>
                <p className="text-slate-500 text-sm leading-relaxed mb-6">{track.description}</p>
                <ul className="space-y-2">
                  {track.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-slate-600 text-sm">
                      <CheckCircle2 size={14} className="text-teal-500 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Enrollment Form */}
      <section className="section-padding" data-testid="enrollment-form-section">
        <div className="container-main">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <span className="font-mono text-xs uppercase tracking-widest text-teal-600 mb-4 block">
                Apply Now
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-slate-900 mb-4">
                Leadership Academy Enrollment Form
              </h2>
              <p className="text-slate-600">
                Complete the form below to apply for the HSL Leadership Academy. 
                Fields marked with <span className="text-teal-600">*</span> are required.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-white border border-slate-200 p-8 md:p-12"
            >
              <form onSubmit={handleSubmit} className="space-y-8" data-testid="enrollment-form">
                {/* Personal Information */}
                <div>
                  <h3 className="font-heading text-lg text-slate-900 mb-6 flex items-center gap-2 pb-2 border-b border-slate-200">
                    <User size={20} className="text-teal-600" />
                    Personal Information
                  </h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="label-mono block mb-2">
                        Full Name <span className="text-teal-600">*</span>
                      </label>
                      <input
                        type="text"
                        name="full_name"
                        value={formData.full_name}
                        onChange={handleInputChange}
                        placeholder="Dr. / Mr. / Ms. Full Name"
                        className="input-hsl"
                        required
                        data-testid="enroll-input-name"
                      />
                    </div>
                    <div>
                      <label className="label-mono block mb-2">
                        Email Address <span className="text-teal-600">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="you@organization.com"
                        className="input-hsl"
                        required
                        data-testid="enroll-input-email"
                      />
                    </div>
                    <div>
                      <label className="label-mono block mb-2">
                        Phone Number <span className="text-teal-600">*</span>
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="+234 XXX XXX XXXX"
                        className="input-hsl"
                        required
                        data-testid="enroll-input-phone"
                      />
                    </div>
                    <div>
                      <label className="label-mono block mb-2">
                        Country <span className="text-teal-600">*</span>
                      </label>
                      <input
                        type="text"
                        name="country"
                        value={formData.country}
                        onChange={handleInputChange}
                        placeholder="Your country of residence"
                        className="input-hsl"
                        required
                        data-testid="enroll-input-country"
                      />
                    </div>
                  </div>
                </div>

                {/* Professional Information */}
                <div>
                  <h3 className="font-heading text-lg text-slate-900 mb-6 flex items-center gap-2 pb-2 border-b border-slate-200">
                    <Briefcase size={20} className="text-teal-600" />
                    Professional Information
                  </h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="label-mono block mb-2">
                        Organization <span className="text-teal-600">*</span>
                      </label>
                      <input
                        type="text"
                        name="organization"
                        value={formData.organization}
                        onChange={handleInputChange}
                        placeholder="Ministry / Organization name"
                        className="input-hsl"
                        required
                        data-testid="enroll-input-organization"
                      />
                    </div>
                    <div>
                      <label className="label-mono block mb-2">
                        Job Title <span className="text-teal-600">*</span>
                      </label>
                      <input
                        type="text"
                        name="job_title"
                        value={formData.job_title}
                        onChange={handleInputChange}
                        placeholder="Your current position"
                        className="input-hsl"
                        required
                        data-testid="enroll-input-jobtitle"
                      />
                    </div>
                    <div>
                      <label className="label-mono block mb-2">
                        Leadership Level <span className="text-teal-600">*</span>
                      </label>
                      <select
                        name="leadership_level"
                        value={formData.leadership_level}
                        onChange={handleInputChange}
                        className="input-hsl"
                        required
                        data-testid="enroll-input-level"
                      >
                        <option value="">Select your level</option>
                        {leadershipLevelOptions.map(option => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="label-mono block mb-2">
                        Sector <span className="text-teal-600">*</span>
                      </label>
                      <select
                        name="sector"
                        value={formData.sector}
                        onChange={handleInputChange}
                        className="input-hsl"
                        required
                        data-testid="enroll-input-sector"
                      >
                        <option value="">Select your sector</option>
                        {sectorOptions.map(option => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="label-mono block mb-2">
                        Years in Health Sector
                      </label>
                      <input
                        type="text"
                        name="years_experience"
                        value={formData.years_experience}
                        onChange={handleInputChange}
                        placeholder="e.g., 10 years"
                        className="input-hsl"
                        data-testid="enroll-input-experience"
                      />
                    </div>
                    <div>
                      <label className="label-mono block mb-2">
                        How did you hear about us?
                      </label>
                      <select
                        name="how_heard"
                        value={formData.how_heard}
                        onChange={handleInputChange}
                        className="input-hsl"
                        data-testid="enroll-input-howheard"
                      >
                        <option value="">Select an option</option>
                        {howHeardOptions.map(option => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Program Selection */}
                <div>
                  <h3 className="font-heading text-lg text-slate-900 mb-6 flex items-center gap-2 pb-2 border-b border-slate-200">
                    <GraduationCap size={20} className="text-teal-600" />
                    Program Selection
                  </h3>
                  <div>
                    <label className="label-mono block mb-2">
                      Preferred Program Track <span className="text-teal-600">*</span>
                    </label>
                    <select
                      name="program_track"
                      value={formData.program_track}
                      onChange={handleInputChange}
                      className="input-hsl"
                      required
                      data-testid="enroll-input-track"
                    >
                      <option value="">Select a program track</option>
                      {programTracks.map(track => (
                        <option key={track.id} value={track.title}>{track.title} ({track.duration})</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Motivation & Expectations */}
                <div>
                  <h3 className="font-heading text-lg text-slate-900 mb-6 flex items-center gap-2 pb-2 border-b border-slate-200">
                    <Target size={20} className="text-teal-600" />
                    Motivation & Expectations
                  </h3>
                  <div className="space-y-6">
                    <div>
                      <label className="label-mono block mb-2">
                        Why do you want to join the Leadership Academy? <span className="text-teal-600">*</span>
                      </label>
                      <textarea
                        name="motivation"
                        value={formData.motivation}
                        onChange={handleInputChange}
                        placeholder="Share your motivation for joining the program and how it aligns with your career goals..."
                        rows={4}
                        className="input-hsl resize-none"
                        required
                        data-testid="enroll-input-motivation"
                      />
                    </div>
                    <div>
                      <label className="label-mono block mb-2">
                        What do you hope to achieve upon completion?
                      </label>
                      <textarea
                        name="expectations"
                        value={formData.expectations}
                        onChange={handleInputChange}
                        placeholder="Describe your expected outcomes and how you plan to apply what you learn..."
                        rows={4}
                        className="input-hsl resize-none"
                        data-testid="enroll-input-expectations"
                      />
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="pt-6 border-t border-slate-200">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-primary w-full md:w-auto flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="enroll-submit-btn"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 size={18} className="animate-spin" />
                        Submitting Enrollment...
                      </>
                    ) : (
                      <>
                        Submit Enrollment Application
                        <ArrowRight size={18} />
                      </>
                    )}
                  </button>
                  <p className="text-slate-500 text-sm mt-4">
                    By submitting this form, you agree to be contacted by HSL regarding your enrollment application.
                  </p>
                </div>
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section-padding bg-slate-100" data-testid="enroll-contact">
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-2xl mx-auto"
          >
            <h3 className="font-heading text-2xl text-slate-900 mb-4">Have Questions?</h3>
            <p className="text-slate-600 mb-6">
              Our admissions team is here to help. Reach out to learn more about the program, 
              eligibility requirements, or the application process.
            </p>
            <Link
              to="/contact"
              className="btn-secondary inline-flex items-center gap-2"
              data-testid="enroll-contact-btn"
            >
              <Mail size={18} />
              Contact Admissions
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
